import { genderOptions } from "keys/gender"
import { useCallback, useContext, useMemo } from "react"
import { UserContext } from "../UserContext"
import { Link, useNavigate, useParams } from "react-router-dom"
import { app } from "router/routes"
import { useMutation } from "@tanstack/react-query"
import { useSelector } from "react-redux"
import { searchUserByTzFetch } from "store/middlewares/user.crud"
import { toast } from "react-toastify"
import { useDispatch } from "react-redux"
import { toastConfig } from "config"
import { validate as validateUser } from '..'
import { submit } from "redux-form"

const useFormFields = () => {
    const { tzApproved, setTzApproved, roles, affiliations, isNewUser, formname } = useContext(UserContext)
    const { userID, affiliationID } = useParams()
    const dispatch = useDispatch()
    const navigate = useNavigate()

    const formValues = useSelector(state => state.form[formname]?.values)
    const { filled_fields } = formValues || {}

    const { mutateAsync: searchUserByTz, isPending: searchUserByTzIsPending } = useMutation({
        mutationFn: searchUserByTzFetch,
        onSuccess: async ({ data }) => {
            if (!data.exists && !userID) {
                toast('המשתמש אינו קיים במערכת, מלא את הפרטים', toastConfig)
                if (affiliationID)
                    navigate(`${app.affiliations}/${affiliationID}/staff/new`)
                else navigate(`${app.users}/new`)
            }
            else {
                // במקרה שהמשתמש כבר קיים, 
                // הכנס לתוך המסך עריכת משתמש מחובר
                if (affiliationID)
                    // במקרה שהגיעו מתוך טאב בתי ספר
                    navigate(`${app.affiliations}/${affiliationID}/staff/${data.id}`)
                else
                    // במקרה שהגיעו ממשתמשים
                    navigate(`${app.users}/${data.id}`)
            }
            setTzApproved(true)
            return data
        },
        enabled: false
    })

    const searchByTz = useCallback(() => {
        const errors = validateUser(formValues || {})
        // בדיקת ולידצייה של תעודת הזהות, והצגת שגיאת טופס
        if (errors.tz)
            dispatch(submit(formname)) // השאירי כדי להציג שגיאות
        // במקרה שתעודה תקינה הבדיקה עוברת לשרת
        else searchUserByTz(formValues.tz)
    }, [formValues])

    let section1Fields = useMemo(() => [
        {
            name: "username",
            component: "input",
            placeholder: "שם משתמש",
            require: true,
            disabled: !tzApproved,
            isLocked: filled_fields?.username
        },
        userID ?
            {
                name: "display_password",
                component: "label",
                disabled: !tzApproved,
                label:
                    <div>
                        {"לא זוכר.ת את הסיסמא?"}
                        <div className="reset-password">
                            {"כדי לאפס את הסיסמא  "}
                            <Link to={app.reset}>{"לחץ כאן"}</Link>
                        </div>
                    </div>
            } :
            {
                name: isNewUser ? "password" : "initial_pass",
                component: "input",
                placeholder: "סיסמא",
                disabled: !isNewUser,
                required: true,
                disabled: !tzApproved,
            }
    ], [userID, tzApproved, filled_fields])

    let section3Fields = useMemo(() => [{
        name: "en_first_name",
        component: "input",
        placeholder: "שם פרטי באנגלית",
        required: true,
        disabled: !tzApproved,
    },
    {
        name: "en_last_name",
        component: "input",
        placeholder: "שם משפחה באנגלית",
        required: true,
        disabled: !tzApproved
    }
    ], [tzApproved])

    const section2Fields = useMemo(() => [{
        name: "first_name",
        component: "input",
        placeholder: "שם פרטי",
        required: true,
        disabled: !tzApproved
    },
    {
        name: "last_name",
        component: "input",
        placeholder: "שם משפחה",
        required: true,
        disabled: !tzApproved
    },
    {
        name: "email",
        component: "input",
        placeholder: "כתובת מייל",
        required: true,
        disabled: !tzApproved,
        isLocked: filled_fields?.email
    },
    {
        name: "phone",
        component: "input",
        placeholder: "טלפון",
        required: true,
        disabled: !tzApproved,
        isLocked: filled_fields?.phone_number // DBלשים לב לגשת בשמות של השדות ב
    },
    {
        name: "gender",
        component: "autoComplete",
        placeholder: "מגדר",
        options: genderOptions,
        required: true,
        disabled: !tzApproved,
    }], [tzApproved, filled_fields])

    const section4Fields = useMemo(() => [{
        name: "tz",
        component: "input",
        placeholder: "תעודת זהות",
        required: true
    },
    {
        name: "search_institution_code",
        component: "button",
        label: "לחצו לבדוק אם המשתמש קיים במערכת",
        color: "thirdly",
        variant: "regular",
        disabled: !formValues?.tz,
        width: 250,
        action: searchByTz,
        loading: searchUserByTzIsPending
    }], [searchUserByTzIsPending, formValues])

    const contactFields = useMemo(() => [{
        name: "is_principal",
        component: "switch",
        label: "הגדר כמנהל"
    },
    {
        name: "is_contact",
        component: "switch",
        label: "הגדר כאיש קשר"
    }
    ], [])

    const roleSectionFields = {
        name: "userRoles",
        className: "user-roles",
        disabled: !tzApproved,
        fields: [
            {
                name: "affiliation",
                className: "affiliation-name",
                component: "autoComplete",
                placeholder: "בית ספר",
                options: affiliations.data,
                addNewItem: true,
            },
            {
                component: "custom",
                customComponent: () => <div className="select-role">
                    <div className="select-role-title">{"אחראי על"}</div>
                    <div>{"ניתן לבחור כמה תפקידים באותו בית ספר"}</div>
                </div>
            },
            {
                name: "roles",
                className: "roles",
                fields: [
                    {
                        name: "role",
                        className: "role-name",
                        placeholder: "שם תפקיד",
                        component: "autoComplete",
                        options: roles,
                        labelKey: "label",
                        addNewItem: true,
                    }
                ]
            }
        ]
    }

    return {
        section1Fields,
        section2Fields,
        section3Fields,
        roleSectionFields,
        contactFields,
        section4Fields
    }
}

export default useFormFields